import { useState, useEffect } from 'react';
import { Button, Space, Table, Tag, message } from 'antd';
import { FilePdfOutlined, EyeOutlined, PlayCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { BPMService } from '../api/services/bpm.service';
import { UsersService } from '../api/services/users.service';
import ProcessDetailModal from './ProcessDetailModal';
import BpmStartModal from './BpmStartModal';
import { useAuth } from '../store/auth';

export default function TaskBPM({ task, project, onUpdate, hideStartButton }) {
  const { user } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [currentProcess, setCurrentProcess] = useState(null);

  useEffect(() => {
    if (task || project) {
      fetchData();
    }
  }, [task, project]);

  const fetchData = async () => {
    try {
      const pRes = task ? await BPMService.getTaskProcesses(task.id) : project ? await BPMService.getProjectProcesses(project.id) : { data: [] };
      const uRes = await UsersService.getUsers();
      
      setProcesses(pRes.data || []);
      setUsers(uRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadPdf = async (process) => {
    try {
      const res = await BPMService.getInstanceSteps(process.id);
      const steps = res.data;
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      
      doc.setFontSize(16);
      doc.text(`Лист согласования к документу: ${process.target_file_name}`, 14, 20);
      doc.setFontSize(12);
      const sourceText = task ? `Связанная задача: ${task.title} (ID: ${task.id})` : project ? `Связанный проект: ${project.name} (ID: ${project.id})` : '';
      doc.text(sourceText, 14, 28);
      
      let y = 40;
      doc.setFontSize(10);
      doc.text('ФИО согласующего', 14, y);
      doc.text('Статус', 80, y);
      doc.text('Дата', 120, y);
      doc.text('ЭЦП (Хэш подписи)', 160, y);
      doc.line(14, y + 2, 280, y + 2);
      
      y += 8;
      steps.forEach(step => {
        const u = users.find(u => String(u.id) === String(step.approver_id));
        const name = u ? u.full_name || u.email : `Сотрудник ID: ${step.approver_id}`;
        doc.text(name, 14, y);
        
        let statusText = step.status === 'pending' ? 'Ожидает' : 
                         step.status === 'approved' ? 'Согласовано' : 'Отклонено';
        doc.text(statusText, 80, y);
        
        if (step.decided_at) {
          doc.text(dayjs(step.decided_at).format('DD.MM.YYYY HH:mm'), 120, y);
        }
        
        if (step.signature_hash) {
            doc.text(step.signature_hash.substring(0, 32) + '...', 160, y);
        } else {
            doc.text('___________________ (Подпись)', 160, y);
        }
        
        y += 8;
        
        if (step.delegatee_id) {
           const del = users.find(u => String(u.id) === String(step.delegatee_id));
           const delName = del ? del.full_name || del.email : `ID: ${step.delegatee_id}`;
           doc.setFontSize(8);
           doc.text(`Исполнитель: ${delName}`, 14, y);
           doc.setFontSize(10);
           y += 6;
        }
      });
      
      doc.save(`Лист_согласования_${process.id}.pdf`);
    } catch (err) {
      message.error('Ошибка при формировании PDF');
    }
  };

  const statusColors = { active: 'processing', completed: 'success', cancelled: 'error', needs_revision: 'warning' };
  
  // Extract all files from task/project for selection
  const allFiles = [];
  const attachments = task?.attachments || project?.attachments || [];
  const comments = task?.comments || project?.comments || [];

  if (attachments) {
      attachments.forEach(a => allFiles.push({ id: a.file_id, name: a.original_name }));
  }
  if (comments) {
      comments.forEach(c => {
          if (c.file_url) {
              const parts = c.file_url.split('/');
              const id = parts[parts.length - 1] || parts[parts.length - 2];
              allFiles.push({ id, name: c.file_name || 'Вложение' });
          }
      });
  }

  const openProcessDetails = (process) => {
    setCurrentProcess(process);
    setDetailModalOpen(true);
  };

  return (
    <div>
      {!hideStartButton && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setStartModalOpen(true)}>
            Запустить процесс согласования
          </Button>
        </div>
      )}

      <Table 
        dataSource={processes} 
        rowKey="id" 
        size="small"
        locale={{ emptyText: 'Нет активных маршрутов согласования' }}
        onRow={(record) => ({
          onClick: () => openProcessDetails(record),
          style: { cursor: 'pointer' }
        })}
        columns={[
          { 
            title: 'Документ', 
            dataIndex: 'target_file_name', 
            key: 'file',
            render: (text, record) => (
              <a 
                style={{ color: '#1890ff' }} 
                href={`/api/v1/files/${record.target_file_id}/nc-url`} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {text}
              </a>
            )
          },
          { title: 'Раунд', dataIndex: 'round_number', key: 'round' },
          { title: 'Срок (дней)', dataIndex: 'duration_days', key: 'duration' },
          { title: 'Этап', dataIndex: 'current_step', key: 'step', render: (s) => <Tag color="purple">Этап {s + 1}</Tag> },
          { title: 'Статус', dataIndex: 'status', key: 'status', render: (s) => <Tag color={statusColors[s] || 'default'}>{s}</Tag> },
          { 
            title: 'Действия', 
            key: 'actions', 
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); openProcessDetails(r); }}>
                  Детали
                </Button>
                <Button size="small" icon={<FilePdfOutlined />} onClick={(e) => { e.stopPropagation(); handleDownloadPdf(r); }}>
                  PDF
                </Button>
              </Space>
            ) 
          }
        ]}
      />
      <ProcessDetailModal 
        process={currentProcess} 
        open={detailModalOpen} 
        onCancel={() => {
          setDetailModalOpen(false);
          setCurrentProcess(null);
        }} 
        users={users}
        onRestartProcess={() => {
            setDetailModalOpen(false);
            fetchData();
            if (onUpdate) onUpdate();
        }}
      />
      {!hideStartButton && (
        <BpmStartModal
           open={startModalOpen}
           onCancel={() => setStartModalOpen(false)}
           onSuccess={() => {
              fetchData();
              if (onUpdate) onUpdate();
           }}
           task={task}
           project={project}
           users={users}
           allFiles={allFiles}
        />
      )}
    </div>
  );
}
